import { FunctionComponent, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { faBookOpen } from "@fortawesome/pro-regular-svg-icons";

import { Icon, PageHeader } from "@/components";
import { Information } from "@/components/Information/Information";

import { ScoreStep } from "./scoreSteps";
import { ScoreTabs } from "./ScoreTabs";

interface ScoreShellProps {
	step: ScoreStep;
	/** The page's one-line purpose, shown as the intro heading. */
	intro?: ReactNode;
	/** A longer explanation, shown behind the info icon beside the intro. */
	description?: JSX.Element | string;
	/** Right-aligned content on the intro row (a help-center link, an action). */
	introActions?: ReactNode;
	/** Help Center article to link from the intro row. */
	helpUrl?: string;
	children: ReactNode;
}

/**
 * The frame every Lead Scoring settings page renders in (PREC-4055): the
 * pinned title bar with the tab strip, then the intro row, then the page.
 *
 * Pinning follows the Analytics header: the outlet has `pt-3`, so the wrapper
 * cancels that padding and hands it back, otherwise the strip pins 12px down
 * and scrolled rows bleed through above it (web rule §12).
 *
 * Its z-index has to clear **100**, not merely the page (PREC-4079): the shared
 * `Tooltip` renders its trigger inside a `relative z-100` wrapper, so every
 * chip with a hover preview — the persona and segment rows, the criterion
 * strip — out-stacked a `z-40` header and scrolled over the tab strip instead
 * of under it. Floating menus (`z-[1600]`) and the tooltip panels themselves
 * stay above this, and the app chrome at `z-[150]` stays above it too.
 */
export const ScoreShell: FunctionComponent<ScoreShellProps> = ({
	step,
	intro,
	description,
	introActions,
	helpUrl,
	children,
}) => {
	const { t } = useTranslation();

	return (
		<div className="flex w-full flex-col pb-8">
			<div className="sticky -top-3 z-[120] -mt-3 w-full bg-white pt-3">
				<PageHeader title={t("leadScoring.pageTitle")}>
					<ScoreTabs currentStep={step} />
				</PageHeader>
			</div>
			{(intro || helpUrl || introActions) && (
				<div className="mb-4 flex items-start gap-3">
					{intro && (
						<div className="flex min-w-0 items-center gap-2">
							{/* Not an <h2>: the global h2 rule in index.css sets 24px and outranks the utility. */}
							<div
								aria-level={2}
								className="text-[17px] font-semibold leading-6 tracking-tight text-text"
								role="heading"
							>
								{intro}
							</div>
							{description && <Information className="shrink-0" information={description} />}
						</div>
					)}
					<div className="ml-auto flex shrink-0 items-center gap-2">
						{introActions}
						{helpUrl && (
							<a
								className="flex h-7 cursor-pointer items-center gap-1.5 rounded-lg border border-border-light bg-white px-2.5 text-xs font-medium text-text hover:bg-background-light"
								href={helpUrl}
								rel="noreferrer"
								target="_blank"
							>
								<Icon className="h-3 w-3" icon={faBookOpen} />
								{t("leadScoring.shell.readHelpArticle")}
							</a>
						)}
					</div>
				</div>
			)}
			{children}
		</div>
	);
};
